<p-confirmPopup></p-confirmPopup>

<div class="grid" *ngIf="lendPeak">
  <div class="col-12">
    <p-card header="Manage Deposits">
      <p-toolbar styleClass="mb-3 gap-2">
        <!-- left: existing actions -->
        <ng-template pTemplate="left">
          <p-button label="Add New Deposit" icon="pi pi-plus" (click)="openDepositDialog()" />
          <p-button
            class="ml-2"
            label="Go to Last Deposit"
            icon="pi pi-arrow-down"
            severity="info"
            (click)="scrollToLastDeposit()"
            [disabled]="lendPeak.depositRecords.length === 0"
          />
          <p-button
            label="Bulk Edit"
            icon="pi pi-pencil"
            class="ml-2 p-button-warning"
            (click)="openBulkEditDialog()"
            [disabled]="lendPeak.depositRecords.length === 0"
          />

          <p-button
            label="Add Ad-hoc Refund"
            icon="pi pi-undo"
            class="ml-2 p-button-warning"
            (click)="openAdhocDialog()"
          />

          <p-button
            label="Delete Selected"
            icon="pi pi-trash"
            severity="danger"
            class="ml-2"
            (click)="confirmDeleteSelected($event)"
            [disabled]="selectedDeposits.length === 0"
          />
        </ng-template>

        <ng-template pTemplate="right">
          <p-button icon="pi pi-external-link" label="Download" (onClick)="downloadDepositsAsCSV()" />
          <p-button
            severity="help"
            label="Copy As CSV"
            icon="pi pi-copy"
            class="ml-2"
            (onClick)="copyDepositsAsCSV()"
          />
          <p-button icon="pi pi-table" severity="contrast" class="ml-2" (click)="depositColumnsDialog()" />
        </ng-template>
      </p-toolbar>

      <!-- Column-picker dialog -->
      <p-dialog
        header="Deposit Columns"
        [modal]="true"
        [(visible)]="showDepositColumnsDialog"
        [style]="{ width: '50%' }"
      >
        <div class="p-fluid">
          <p-picklist
            [source]="availableDepositCols"
            [target]="selectedDepositCols"
            [dragdrop]="true"
            filterBy="header"
            sourceFilterPlaceholder="Search by name"
            targetFilterPlaceholder="Search by name"
            breakpoint="1400px"
            [sourceStyle]="{ height: '30rem' }"
            [targetStyle]="{ height: '30rem' }"
            scrollHeight="30rem"
          >
            <ng-template let-col #item>{{ col.header }}</ng-template>
          </p-picklist>
        </div>
        <p-divider></p-divider>
        <div class="flex justify-content-end gap-2">
          <p-button label="Cancel" severity="secondary" (click)="showDepositColumnsDialog = false" />
          <p-button label="Reset" severity="secondary" (click)="resetDepositColumns()" />
          <p-button label="Save" (click)="saveDepositColumns()" />
        </div>
      </p-dialog>

      <p-divider></p-divider>
      <div class="p-fluid">
        <p-table
          *ngIf="lendPeak.depositRecords.length"
          #dtDeposits
          [value]="lendPeak.depositRecords._records"
          [columns]="selectedDepositCols"
          editMode="cell"
          class="p-datatable-sm"
          [scrollable]="true"
          scrollHeight="600px"
          [(selection)]="selectedDeposits"
          selectionMode="multiple"
          sortField="jsEffectiveDate"
          [sortOrder]="-1"
          stateStorage="session"
        >
          <!-- dynamic header -->
          <!-- header template in your <p-table> -->
          <ng-template #header let-columns>
            <tr>
              <th style="width: 3rem"><p-tableHeaderCheckbox /></th>

              <ng-container *ngFor="let c of columns">
                <!-- give the directive a field when sortable, otherwise null -->
                <th [pSortableColumn]="c.sortable ? c.field : null">
                  <!-- label -->
                  <span class="p-column-title">{{ c.header }}</span>

                  <!-- chevron (only if sortable) -->
                  <p-sortIcon *ngIf="c.sortable" [field]="c.field"></p-sortIcon>

                  <!-- funnel (only if filter metadata present) -->
                  <p-columnFilter *ngIf="c.filter" [field]="c.field" [type]="c.filter" display="menu"></p-columnFilter>
                </th>
              </ng-container>

              <th>Actions</th>
            </tr>
          </ng-template>
          <!-- dynamic body -->
          <ng-template #body let-row let-columns="columns">
            <tr
              [ngClass]="{
                'selected-row': isDepositHighlighted(row),
                'bg-yellow-50': row.metadata?.type === 'adhoc_refund',
              }"
            >
              <td style="width: 3rem"><p-tableCheckbox [value]="row" /></td>

              <!-- iterate chosen cols -->
              <ng-container *ngFor="let c of columns">
                <td [ngSwitch]="c.field">
                  <!-- add the cases you need; a few common ones shown -->
                  <ng-container *ngSwitchCase="'active'">
                    <p-toggleswitch [(ngModel)]="row.active" (ngModelChange)="depositActiveUpdated()" />
                  </ng-container>

                  <ng-container *ngSwitchCase="'id'">
                    <span [class.opacity-30]="!row.active || dateIsInTheFutureFromSnapshotDate(row.effectiveDate)">
                      {{ row.id }}
                    </span>

                    <p-badge
                      *ngIf="totalRefunds(row) > 0"
                      [value]="activeRefunds(row) + '/' + totalRefunds(row) + ' Refunds'"
                      [severity]="activeRefunds(row) > 0 ? 'contrast' : 'secondary'"
                      styleClass="mr-2"
                      pTooltip="Active / Total refunds"
                      tooltipPosition="top"
                    />

                    <p-badge
                      *ngIf="row.metadata?.type === 'auto_close'"
                      value="Auto Close"
                      severity="info"
                      styleClass="mr-2"
                      pTooltip="System-generated payment that zeros the remaining balance"
                      tooltipPosition="top"
                    ></p-badge>

                    <!-- -- multiple-bills badge -->
                    <p-badge
                      *ngIf="row.usageDetails?.length > 1"
                      value="{{ row.usageDetails.length + ' Bills' }}"
                      severity="warn"
                      styleClass="mr-2"
                      pTooltip="This deposit covers multiple bills"
                      tooltipPosition="top"
                    ></p-badge>

                    <p-badge
                      *ngIf="row.metadata?.type === 'adhoc_refund'"
                      value="Ad-hoc Refund"
                      severity="warn"
                      styleClass="mr-2"
                      pTooltip="This is a manually-entered refund record"
                    ></p-badge>

                    <!-- -- future badge -->
                    <p-badge
                      *ngIf="dateIsInTheFutureFromSnapshotDate(row.effectiveDate)"
                      value="Future"
                      severity="warn"
                      styleClass="mr-2"
                      pTooltip="Deposit's effective date is after the snapshot date"
                      tooltipPosition="top"
                      tooltipStyleClass="wide-tooltip"
                    ></p-badge>

                    <!-- -- inactive badge -->
                    <p-badge
                      *ngIf="!row.active"
                      value="Inactive"
                      severity="danger"
                      styleClass="mr-2"
                      pTooltip="Deposit is inactive"
                      tooltipPosition="top"
                      tooltipStyleClass="wide-tooltip"
                    ></p-badge>

                    <!-- -- static-allocation badge -->
                    <p-badge
                      *ngIf="row.staticAllocation"
                      value="Static"
                      severity="info"
                      styleClass="mr-2"
                      pTooltip="Deposit is using static amounts allocation
              Principal: {{ row.staticAllocation.jsPrincipal | currency }}
              Interest:  {{ row.staticAllocation.jsInterest | currency }}
              Fees:      {{ row.staticAllocation.jsFees | currency }}
              Prepayment:{{ row.staticAllocation.jsPrepayment | currency }}"
                      tooltipPosition="top"
                      tooltipStyleClass="wide-tooltip"
                    ></p-badge>
                  </ng-container>

                  <ng-container *ngSwitchCase="'amount'">{{ row.jsAmount | currency }}</ng-container>

                  <ng-container *ngSwitchCase="'jsEffectiveDate'">
                    {{ row.jsEffectiveDate | date: 'yyyy-MM-dd' }}
                  </ng-container>

                  <ng-container *ngSwitchCase="'jsSystemDate'">
                    {{ row.jsSystemDate | date: 'YYYY-MM-DD hh:mm:ss' }}
                  </ng-container>

                  <ng-container *ngSwitchCase="'jsClearingDate'">
                    {{ row.jsClearingDate | date: 'yyyy-MM-dd' }}
                  </ng-container>

                  <ng-container *ngSwitchCase="'allocatedTotal'">
                    {{ row.allocatedTotal.toNumber() | currency }}
                  </ng-container>

                  <ng-container *ngSwitchCase="'jsActiveRefundAmount'">
                    {{ row.jsActiveRefundAmount | currency }}
                  </ng-container>

                  <ng-container *ngSwitchCase="'allocatedPrincipal'">
                    {{ row.allocatedPrincipal.toNumber() | currency }}
                  </ng-container>

                  <ng-container *ngSwitchCase="'allocatedInterest'">
                    {{ row.allocatedInterest.toNumber() | currency }}
                  </ng-container>

                  <ng-container *ngSwitchCase="'allocatedFees'">
                    {{ row.allocatedFees.toNumber() | currency }}
                  </ng-container>

                  <ng-container *ngSwitchCase="'jsUnusedAmount'">
                    <span [class.font-semibold]="row.active && row.unusedAmount.greaterThan(0)">
                      {{ row.jsUnusedAmount | currency }}
                    </span>
                  </ng-container>

                  <ng-container *ngSwitchCase="'applyExcessToPrincipal'">
                    <!-- green check  /  orange minus -->
                    <i
                      class="pi"
                      [ngClass]="{
                        'pi-check-circle': row.applyExcessToPrincipal,
                        'pi-minus-circle': !row.applyExcessToPrincipal,
                      }"
                      [style.color]="row.applyExcessToPrincipal ? 'green' : 'orange'"
                    ></i>

                    <!-- badges only when excess-to-principal is on -->
                    <span *ngIf="row.applyExcessToPrincipal">
                      <!-- prepayment amount badge -->
                      <p-badge
                        *ngIf="row.excessAmountApplied.greaterThan(0)"
                        value="Prepayment: {{ row.excessAmountApplied.toNumber() | currency }}"
                        severity="success"
                        badgeSize="large"
                        styleClass="ml-2"
                        pTooltip="Deposit has pre-payment of {{
                          row.excessAmountApplied.toNumber() | currency
                        }} applied"
                        tooltipPosition="top"
                        tooltipStyleClass="wide-tooltip"
                      ></p-badge>

                      <!-- zero-excess badge -->
                      <p-badge
                        *ngIf="row.excessAmountApplied.isZero()"
                        value="No Excess Available"
                        severity="warn"
                        styleClass="ml-2"
                        pTooltip="Deposit has pre-payment of {{
                          row.excessAmountApplied.toNumber() | currency
                        }} applied"
                        tooltipPosition="top"
                        tooltipStyleClass="wide-tooltip"
                      ></p-badge>
                    </span>
                  </ng-container>

                  <!-- default -->
                  <ng-container *ngSwitchDefault>{{ row[c.field] }}</ng-container>
                </td>
              </ng-container>

              <!-- actions col (static) -->
              <td class="flex gap-1">
                <!-- View usage – always enabled -->
                <p-button
                  icon="pi pi-eye"
                  severity="info"
                  styleClass="p-button-lg p-button-text"
                  (click)="viewDepositUsageDetails(row)"
                  pTooltip="View Usage"
                ></p-button>

                <!-- Ellipsis trigger -->
                <p-button
                  icon="pi pi-ellipsis-v"
                  severity="secondary"
                  styleClass="p-button-lg p-button-text"
                  (click)="openRowMenu($event, row)"
                  pTooltip="More actions"
                ></p-button>
              </td>
            </tr>
          </ng-template>

          <ng-template #footer let-columns>
            <tr>
              <!-- selection-checkbox column → leave empty -->
              <td class="bg-blue-50"></td>

              <!-- iterate through the same dynamic columns so everything lines up -->
              <ng-container *ngFor="let c of columns; let i = index">
                <td class="bg-blue-50 font-bold" [ngSwitch]="c.field">
                  <!-- put the label in the first non-checkbox cell -->
                  <ng-container *ngSwitchDefault>
                    <span *ngIf="i === 0">Totals:</span>
                  </ng-container>

                  <!-- dollar-value columns -->
                  <ng-container *ngSwitchCase="'amount'">
                    {{ totalAmount | currency }}
                  </ng-container>
                  <ng-container *ngSwitchCase="'jsUnusedAmount'">
                    {{ totalUnusedAmount | currency }}
                  </ng-container>
                  <ng-container *ngSwitchCase="'jsActiveRefundAmount'">
                    {{ totalRefunded | currency }}
                  </ng-container>
                  <ng-container *ngSwitchCase="'allocatedTotal'">
                    {{ totalAllocatedTotal | currency }}
                  </ng-container>
                  <ng-container *ngSwitchCase="'allocatedPrincipal'">
                    {{ totalAllocatedPrincipal | currency }}
                  </ng-container>
                  <ng-container *ngSwitchCase="'allocatedInterest'">
                    {{ totalAllocatedInterest | currency }}
                  </ng-container>
                  <ng-container *ngSwitchCase="'allocatedFees'">
                    {{ totalAllocatedFees | currency }}
                  </ng-container>
                </td>
              </ng-container>

              <!-- actions column → leave empty -->
              <td class="bg-blue-50"></td>
            </tr>
          </ng-template>
        </p-table>
        <p-menu #rowMenu [popup]="true" appendTo="body"></p-menu>
      </div>
    </p-card>
  </div>
</div>

<!-- Deposit Add/Edit Dialog -->
<p-dialog
  header="{{ selectedDepositForEdit ? 'Edit Deposit' : 'Add New Deposit' }}"
  [(visible)]="showDepositDialog"
  [modal]="true"
  [closable]="true"
  (onHide)="onDepositDialogHide()"
>
  <div class="p-grid p-nogutter" *ngIf="depositData">
    <div class="field">
      <label for="amount-{{ depositData.id }}">Amount</label>

      <p-inputgroup>
        <p-inputgroup-addon>$</p-inputgroup-addon>

        <p-inputNumber
          #amountCtrl="ngModel"
          inputId="amount-{{ depositData.id }}"
          [(ngModel)]="depositData.jsAmount"
          mode="decimal"
          minFractionDigits="2"
          maxFractionDigits="2"
          (ngModelChange)="onDataChange($event)"
          required
          class="w-full"
          inputStyleClass="w-full"
        ></p-inputNumber>

        <!-- calculator toggle -->
        <p-inputgroup-addon (click)="openCalc(amountCtrl)">
          <i class="pi pi-calculator"></i>
        </p-inputgroup-addon>
      </p-inputgroup>
    </div>
    <div class="formgrid grid">
      <div class="field col">
        <label for="effectiveDate-{{ depositData.id }}">Effective Date</label>
        <p-datepicker
          inputId="effectiveDate-{{ depositData.id }}"
          [(ngModel)]="depositData.jsEffectiveDate"
          (ngModelChange)="onDataChange($event)"
          dateFormat="yy-mm-dd"
          [showClear]="false"
          [showButtonBar]="true"
          showIcon
          required
          appendTo="body"
          inputStyleClass="w-full"
          class="w-full"
          [style]="{ width: '100%' }"
          [inputStyle]="{
            width: '100%',
          }"
        >
          <ng-template pTemplate="date" let-date>
            <span
              [ngStyle]="
                this.lendPeak?.amortization?.isPeriodEndDate(date)
                  ? {
                      backgroundColor: 'orange',
                      color: 'white',
                      borderRadius: '50%',
                      padding: '5px',
                      display: 'inline-block',
                      textAlign: 'center',
                      width: '24px',
                    }
                  : {}
              "
            >
              {{ date.day }}
            </span>
          </ng-template>
        </p-datepicker>
      </div>
      <div class="field col">
        <label for="clearingDate-{{ depositData.id }}">Clearing Date</label>
        <p-datepicker
          inputId="clearingDate-{{ depositData.id }}"
          [(ngModel)]="depositData.jsClearingDate"
          (ngModelChange)="onDataChange($event)"
          [minDate]="depositData.jsEffectiveDate"
          dateFormat="yy-mm-dd"
          appendTo="body"
          [showButtonBar]="true"
          showIcon
          inputStyleClass="w-full"
          class="w-full"
          [style]="{ width: '100%' }"
          [inputStyle]="{
            width: '100%',
          }"
        >
          <ng-template pTemplate="date" let-date>
            <span
              [ngStyle]="
                this.lendPeak?.amortization?.isPeriodEndDate(date)
                  ? {
                      backgroundColor: 'orange',
                      color: 'white',
                      borderRadius: '50%',
                      padding: '5px',
                      display: 'inline-block',
                      textAlign: 'center',
                      width: '24px',
                    }
                  : {}
              "
            >
              {{ date.day }}
            </span>
          </ng-template>
        </p-datepicker>
      </div>
    </div>
    <div class="field">
      <label for="excessAppliedDate-{{ depositData.id }}">Excess Applied Date</label>

      <p-inputgroup>
        <p-inputgroup-addon>
          <p-checkbox
            inputId="applyExcessToPrincipal"
            [(ngModel)]="depositData.applyExcessToPrincipal"
            name="applyExcessToPrincipal"
            binary="true"
            size="small"
            (onChange)="onApplyExcessToPrincipalChange($event)"
          ></p-checkbox>
        </p-inputgroup-addon>

        <p-datepicker
          inputId="excessAppliedDate-{{ depositData.id }}"
          [disabled]="!depositData.applyExcessToPrincipal"
          [(ngModel)]="depositData.jsExcessAppliedDate"
          (ngModelChange)="onDataChange($event)"
          name="excessAppliedDate"
          dateFormat="yy-mm-dd"
          [showButtonBar]="true"
          [minDate]="depositData.jsEffectiveDate"
          [required]="depositData.applyExcessToPrincipal"
          appendTo="body"
          [style]="{ width: '100%' }"
          appendTo="body"
          [inputStyle]="{
            width: '100%',
            'border-radius': 'unset',
            'font-size': '1.1rem',
          }"
          class="w-full"
        >
          <ng-template pTemplate="date" let-date>
            <span
              [ngStyle]="
                this.lendPeak?.amortization?.isPeriodEndDate(date)
                  ? {
                      backgroundColor: 'orange',
                      color: 'white',
                      borderRadius: '50%',
                      padding: '5px',
                      display: 'inline-block',
                      textAlign: 'center',
                      width: '24px',
                    }
                  : {}
              "
            >
              {{ date.day }}
            </span>
          </ng-template>
        </p-datepicker>

        <p-inputgroup-addon>
          <i class="pi pi-calendar"></i>
        </p-inputgroup-addon>
      </p-inputgroup>
    </div>
    <div class="field">
      <div class="flex items-center gap-2">
        <p-checkbox
          [(ngModel)]="depositData.jsApplyExcessAtTheEndOfThePeriod"
          inputId="applyExcessAtTheEndOfThePeriod"
          name="size"
          binary="true"
          size="small"
        />
        <label for="size_large">
          Apply payment at period's end date during open billing cycle
          <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, applyExcessAtTheEndOfThePeriod)"></i>
        </label>
        <p-popover #applyExcessAtTheEndOfThePeriod>
          <ng-template pTemplate="content">
            <div class="flex flex-column gap-2 text-sm line-height-3" style="max-width: 320px">
              <p class="m-0">
                <strong>Apply Payment at Period's End Date:</strong>
              </p>
              <p class="m-0">
                When this setting is enabled and a bill is already open, any extra or "excess" payment will be applied
                <em>at the start of the next billing cycle</em>
                . This prevents re-issuing or closing the current open bill, but it also means you won't see immediate
                interest reduction on the current statement.
              </p>
              <p class="m-0">
                However, if the bill is
                <em>not open yet</em>
                , any principal prepayment is applied right away using its excess-applied date value.
              </p>
            </div>
          </ng-template>
        </p-popover>
      </div>
    </div>
    <div class="field">
      <label for="allocationType">Allocation Type</label>
      <p-select
        inputId="allocationType"
        [(ngModel)]="selectedAllocationType"
        (ngModelChange)="onAllocationTypeChange($event)"
        [options]="allocationTypes"
        optionLabel="label"
        optionValue="value"
        required
        appendTo="body"
        inputStyleClass="w-full"
        class="w-full"
      ></p-select>
    </div>
    <p-card *ngIf="selectedAllocationType === 'staticDistribution' && depositData.staticAllocation">
      <div class="field w-full text-xl text-center">
        <span class="text-300">Remaining Amount:</span>
        <span
          class="font-medium"
          [class]="{
            'text-red-500': staticUnusedAmount != 0,
          }"
        >
          {{ staticUnusedAmount | currency }}
        </span>
      </div>

      <div class="formgrid grid">
        <div class="field col">
          <label for="staticPrincipal-{{ depositData.id }}">Principal</label>
          <p-inputgroup>
            <p-inputgroup-addon>$</p-inputgroup-addon>
            <p-inputNumber
              inputId="staticPrincipal-{{ depositData.id }}"
              [(ngModel)]="depositData.staticAllocation.jsPrincipal"
              mode="decimal"
              minFractionDigits="2"
              maxFractionDigits="2"
              (ngModelChange)="onDataChange($event)"
              required
              class="w-full"
              inputStyleClass="w-full"
              [min]="0"
            ></p-inputNumber>
          </p-inputgroup>
        </div>
        <div class="field col">
          <label for="staticInterest-{{ depositData.id }}">Interest</label>
          <p-inputgroup>
            <p-inputgroup-addon>$</p-inputgroup-addon>
            <p-inputNumber
              inputId="staticInterest-{{ depositData.id }}"
              [(ngModel)]="depositData.staticAllocation.jsInterest"
              mode="decimal"
              minFractionDigits="2"
              maxFractionDigits="2"
              (ngModelChange)="onDataChange($event)"
              required
              class="w-full"
              inputStyleClass="w-full"
              [min]="0"
            ></p-inputNumber>
          </p-inputgroup>
        </div>
      </div>

      <div class="formgrid grid">
        <div class="field col">
          <label for="staticFees-{{ depositData.id }}">Fees</label>
          <p-inputgroup>
            <p-inputgroup-addon>$</p-inputgroup-addon>
            <p-inputNumber
              inputId="staticFees-{{ depositData.id }}"
              [(ngModel)]="depositData.staticAllocation.jsFees"
              mode="decimal"
              minFractionDigits="2"
              maxFractionDigits="2"
              (ngModelChange)="onDataChange($event)"
              required
              class="w-full"
              inputStyleClass="w-full"
              [min]="0"
            ></p-inputNumber>
          </p-inputgroup>
        </div>
        <div class="field col">
          <label for="staticPrepayment-{{ depositData.id }}">Principal Prepayment</label>

          <p-inputgroup>
            <p-inputgroup-addon>$</p-inputgroup-addon>

            <p-inputNumber
              inputId="staticPrepayment-{{ depositData.id }}"
              [(ngModel)]="depositData.staticAllocation.jsPrepayment"
              mode="decimal"
              minFractionDigits="2"
              maxFractionDigits="2"
              (ngModelChange)="onDataChange($event)"
              required
              class="w-full"
              inputStyleClass="w-full"
              [min]="0"
            ></p-inputNumber>
          </p-inputgroup>
        </div>
      </div>
    </p-card>
  </div>

  <div class="flex justify-content-end gap-2">
    <p-button
      label="Cancel"
      severity="warn"
      icon="pi pi-times"
      (click)="onDepositDialogHide()"
      class="p-button-text"
    ></p-button>
    <p-button
      label="Save"
      icon="pi pi-check"
      type="submit"
      [disabled]="staticUnusedAmount != 0"
      (click)="saveDeposit()"
    ></p-button>
  </div>
</p-dialog>

<!-- Deposit Usage Details Dialog -->
<p-dialog
  [(visible)]="showDepositUsageDetailsDialog"
  [modal]="true"
  [style]="{ width: '85%' }"
  [closable]="true"
  (onHide)="selectedDeposit = null"
  *ngIf="selectedDeposit && lendPeak"
>
  <ng-template #header>
    <div *ngIf="selectedDeposit" class="inline-flex items-center justify-center">
      <span class="font-semibold whitespace-nowrap pr-5">Deposit Usage Details: {{ selectedDeposit.id }}</span>
      <!-- Display a badge if there's more than one usage detail -->
      <p-badge
        *ngIf="(selectedDeposit?.usageDetails || []).length > 1"
        value="{{ selectedDeposit.usageDetails.length + ' Bills' }}"
        severity="warn"
        styleClass="mr-2"
        pTooltip="This deposit covers multiple bills"
        tooltipPosition="top"
      ></p-badge>
      <p-badge
        *ngIf="effectiveDateIsAfterSnapshotDate(selectedDeposit)"
        value="Future"
        severity="warn"
        styleClass="mr-2"
        pTooltip="Deposit's effective date is after the snapshot date"
        tooltipPosition="top"
        tooltipStyleClass="wide-tooltip"
      ></p-badge>
      <p-badge
        *ngIf="!selectedDeposit?.active"
        value="Inactive"
        severity="danger"
        styleClass="mr-2"
        pTooltip="Deposit is inactive"
        tooltipPosition="top"
        tooltipStyleClass="wide-tooltip"
      ></p-badge>
      <p-badge
        *ngIf="selectedDeposit?.staticAllocation"
        value="Static"
        severity="info"
        styleClass="mr-2"
        pTooltip="Deposit is using static amounts allocation"
        tooltipPosition="top"
        tooltipStyleClass="wide-tooltip"
      ></p-badge>
    </div>
  </ng-template>
  <p-table [value]="selectedDeposit.usageDetails || []">
    <!-- TABLE HEADER -->
    <ng-template pTemplate="header">
      <tr>
        <th>Period</th>
        <th>Bill Due Date</th>
        <th>Bill ID</th>
        <!-- Bill Tags/Badges column -->
        <th></th>
        <!-- Allocated Total column -->
        <th>Allocated Total</th>
        <th>Allocated Principal</th>
        <th>Allocated Interest</th>
        <th>Allocated Fees</th>
        <th>Date</th>
      </tr>

      <!-- Original Deposit row -->
      <tr>
        <th class="bg-blue-50"></th>
        <th class="bg-blue-50"></th>
        <th class="bg-blue-50 text-right">Original Deposit:</th>
        <!-- blank col for Bill Tags -->
        <th class="bg-blue-50"></th>
        <!-- deposit.jsAmount => goes in Allocated Total column -->
        <th class="bg-blue-50 font-bold">
          {{ selectedDeposit.jsAmount | currency }}
        </th>
        <!-- 0 for principal, interest, fees -->
        <th class="bg-blue-50 font-bold">{{ 0 | currency }}</th>
        <th class="bg-blue-50 font-bold">{{ 0 | currency }}</th>
        <th class="bg-blue-50 font-bold">{{ 0 | currency }}</th>
        <!-- Blank for Date -->
        <th class="bg-blue-50"></th>
      </tr>
    </ng-template>

    <!-- TABLE BODY -->
    <ng-template pTemplate="body" let-detail>
      <tr>
        <td>{{ detail.period }}</td>
        <td>{{ detail.billDueDate.toString() | date: 'yyyy-MM-dd' }}</td>
        <td>
          <!-- Bill ID + button to open Bill Card -->
          <p-button
            (click)="viewBillCard(detail.billId)"
            icon="pi pi-search"
            [rounded]="true"
            [text]="true"
            severity="success"
            class="pr-2"
          ></p-button>
          {{ detail.billId }}
        </td>

        <!-- Bill Tag column -->
        <td>
          <ng-container *ngIf="lendPeak.bills.getBillById(detail.billId) as bill">
            <div class="flex align-items-center gap-2 mt-1">
              <!-- If more than 1 payment covers this bill -->
              <p-tag
                *ngIf="(bill.paymentDetails?.length ?? 0) > 1"
                value="{{ bill.paymentDetails.length + ' Payments' }}"
                severity="warn"
                pTooltip="This bill was satisfied by multiple payments"
                tooltipPosition="top"
              ></p-tag>

              <!-- If bill is open and not paid -->
              <p-tag *ngIf="bill.isOpen(snapshotDate) && !bill.isPaid()" value="Open Bill" severity="success"></p-tag>

              <!-- If bill is future (not open) and not paid -->
              <p-tag *ngIf="!bill.isOpen(snapshotDate) && !bill.isPaid()" value="Future" severity="info"></p-tag>

              <!-- If total due is 0 => 'Skip A Pay' -->
              <p-tag *ngIf="bill.jsOriginalTotalDue == 0" value="Skip A Pay" severity="secondary"></p-tag>

              <!-- If total due > 0 => 'Paid' or 'Unpaid' -->
              <p-tag
                *ngIf="bill.jsTotalDue > 0"
                [value]="bill.isPaid() ? 'Paid' : 'Unpaid'"
                [severity]="bill.isPaid() ? 'success' : 'warn'"
              ></p-tag>

              <!-- If past due and not paid => e.g. 'X Days Past Due' -->
              <p-tag
                *ngIf="bill.isPastDue(snapshotDate)"
                [value]="bill.getDaysPastDue(snapshotDate) + ' Days Past Due'"
                severity="danger"
              ></p-tag>

              <!-- If the bill is paid and has a satisfied date, check days late/early/on-time -->
              <ng-container *ngIf="bill.isPaid() && bill.dateFullySatisfied">
                <p-tag *ngIf="bill.daysLate" [value]="bill.daysLate + ' Days Late'" severity="warn"></p-tag>
                <p-tag *ngIf="bill.daysEarly" [value]="bill.daysEarly + ' Days Early'" severity="success"></p-tag>
                <p-tag *ngIf="!bill.daysLate && !bill.daysEarly" value="On-Time" severity="info"></p-tag>
              </ng-container>
            </div>
          </ng-container>
        </td>

        <!-- Allocated Total (principal + interest + fees) => we call a TS method -->
        <td>{{ getUsageRowTotal(detail) | currency }}</td>
        <td>{{ detail.allocatedPrincipal.toNumber() | currency }}</td>
        <td>{{ detail.allocatedInterest.toNumber() | currency }}</td>
        <td>{{ detail.allocatedFees.toNumber() | currency }}</td>
        <td>{{ detail.date.toString() | date: 'yyyy-MM-dd' }}</td>
      </tr>
    </ng-template>

    <!-- TABLE FOOTER (ALLOCATED & UNUSED) -->
    <ng-template pTemplate="footer">
      <!-- ALLOCATED row -->
      <tr>
        <td class="bg-blue-50" colspan="4" style="text-align: right; font-weight: bold">Allocated:</td>
        <td class="bg-blue-50 font-bold">
          {{ allocatedTotalSum | currency }}
        </td>
        <td class="bg-blue-50 font-bold">
          {{ allocatedPrincipalSum | currency }}
        </td>
        <td class="bg-blue-50 font-bold">
          {{ allocatedInterestSum | currency }}
        </td>
        <td class="bg-blue-50 font-bold">
          {{ allocatedFeesSum | currency }}
        </td>
        <td class="bg-blue-50"></td>
      </tr>

      <!-- UNUSED row -->
      <tr>
        <td class="bg-blue-50" colspan="4" style="text-align: right; font-weight: bold">Unused:</td>
        <td class="bg-blue-50 font-bold">
          {{ selectedDepositUnusedAmount() | currency }}
        </td>
        <td class="bg-blue-50 font-bold"></td>
        <td class="bg-blue-50 font-bold"></td>
        <td class="bg-blue-50 font-bold"></td>
        <td class="bg-blue-50"></td>
      </tr>
    </ng-template>
  </p-table>
</p-dialog>

<!-- BULK EDIT ADDITION: Bulk Edit Dialog -->
<p-dialog
  header="Bulk Edit Deposits"
  [(visible)]="showBulkEditDialog"
  [modal]="true"
  [closable]="true"
  [style]="{ width: '400px' }"
>
  <form (ngSubmit)="applyBulkEdit()">
    <div class="p-grid p-nogutter">
      <div class="field">
        <label for="bulkAllocationType">Allocation Type</label>
        <p-inputgroup>
          <p-inputgroup-addon>
            <p-checkbox
              id="bulkDepositEditAllocationType"
              name="bulkDepositEditAllocationType"
              [(ngModel)]="bulkDepositEditAllocationType"
              binary="true"
              inputId="bulkDepositEditAllocationType"
              size="small"
            ></p-checkbox>
          </p-inputgroup-addon>

          <p-select
            id="bulkAllocationType"
            name="bulkAllocationType"
            [disabled]="!bulkDepositEditAllocationType"
            [(ngModel)]="bulkAllocationType"
            [options]="bulkEditAllocationTypes"
            optionLabel="label"
            optionValue="value"
            [showClear]="false"
            appendTo="body"
            inputStyleClass="w-full"
          ></p-select>
        </p-inputgroup>
      </div>
      <div class="field">
        <label for="bulkAllocationType">Apply Excess to Principal</label>
        <p-inputgroup>
          <p-inputgroup-addon>
            <p-checkbox
              id="bulkDepositEditApplyExccessToPrincipal"
              name="bulkDepositEditApplyExccessToPrincipal"
              [(ngModel)]="bulkDepositEditApplyExccessToPrincipal"
              binary="true"
              inputId="bulkDepositEditApplyExccessToPrincipal"
              size="small"
            ></p-checkbox>
          </p-inputgroup-addon>

          <p-select
            id="bulkApplyExcessToPrincipal"
            name="bulkApplyExcessToPrincipal"
            [disabled]="!bulkDepositEditApplyExccessToPrincipal"
            [(ngModel)]="bulkApplyExccessToPrincipal"
            [options]="bulkApplyExccessToPrincipalTypes"
            optionLabel="label"
            optionValue="value"
            [showClear]="false"
            appendTo="body"
            inputStyleClass="w-full"
          ></p-select>
        </p-inputgroup>
      </div>
    </div>

    <div class="flex justify-content-end mt-3">
      <p-button label="Cancel" icon="pi pi-times" (click)="showBulkEditDialog = false" class="p-button-text"></p-button>
      <p-button label="Save" icon="pi pi-check" class="pl-2 p-button-warning" type="submit"></p-button>
    </div>
  </form>
</p-dialog>

<!-- Bill Card Dialog (Non-blocking, draggable) -->
<p-dialog
  [(visible)]="showBillCardDialog"
  [modal]="false"
  [draggable]="true"
  [resizable]="true"
  [style]="{ width: '650px' }"
  [header]="'Bill Card - ' + (selectedBillForCard?.id || '')"
  (onHide)="selectedBillForCard = null"
>
  <ng-container *ngIf="selectedBillForCard as bill">
    <!-- 1) Single row of badges right under the dialog header -->
    <div class="flex align-items-center gap-2 mt-1 flex-wrap">
      <!-- If more than 1 payment covers this bill -->
      <p-tag
        *ngIf="(bill.paymentDetails?.length ?? 0) > 1"
        [value]="bill.paymentDetails.length + ' Payments'"
        severity="warn"
        pTooltip="This bill was satisfied by multiple payments"
        tooltipPosition="top"
      ></p-tag>

      <!-- If bill is open and not paid -->
      <p-tag *ngIf="bill.isOpen(snapshotDate) && !bill.isPaid()" value="Open Bill" severity="success"></p-tag>

      <!-- If bill is future (not open) and not paid -->
      <p-tag *ngIf="!bill.isOpen(snapshotDate) && !bill.isPaid()" value="Future" severity="info"></p-tag>

      <!-- If total due is 0 => 'Skip A Pay' -->
      <p-tag *ngIf="bill.jsTotalDue == 0" value="Skip A Pay" severity="secondary"></p-tag>

      <!-- If total due > 0 => 'Paid' or 'Unpaid' -->
      <p-tag
        *ngIf="bill.jsTotalDue > 0"
        [value]="bill.isPaid() ? 'Paid' : 'Unpaid'"
        [severity]="bill.isPaid() ? 'success' : 'warn'"
      ></p-tag>

      <!-- If past due and not paid => e.g. 'X Days Past Due' -->
      <p-tag
        *ngIf="bill.isPastDue(snapshotDate)"
        [value]="bill.getDaysPastDue(snapshotDate) + ' Days Past Due'"
        severity="danger"
      ></p-tag>

      <!-- If the bill is paid + dateFullySatisfied => check days late/early/on-time -->
      <ng-container *ngIf="bill.isPaid() && bill.dateFullySatisfied">
        <!-- If daysLate > 0 => 'X Days Late' -->
        <p-tag *ngIf="bill.daysLate" [value]="bill.daysLate + ' Days Late'" severity="warn"></p-tag>

        <!-- If daysEarly > 0 => 'X Days Early' -->
        <p-tag *ngIf="bill.daysEarly" [value]="bill.daysEarly + ' Days Early'" severity="success"></p-tag>

        <!-- Otherwise => 'On-Time' -->
        <p-tag *ngIf="!bill.daysLate && !bill.daysEarly" value="On-Time" severity="info"></p-tag>
      </ng-container>
    </div>

    <!-- 2) Bill data inside a p-card -->
    <p-card styleClass="mt-3">
      <!-- Row 1: Period / Due Date -->
      <div class="grid text-sm gap-2">
        <div class="col-12 md:col-6 flex align-items-center">
          <span class="font-semibold text-300 mr-1">Period:</span>
          <span>{{ bill.period }}</span>
        </div>

        <div class="col-12 md:col-5 flex align-items-center">
          <span class="font-semibold text-300 mr-1">Due Date:</span>
          <span>{{ bill.jsDueDate | date: 'yyyy-MM-dd' }}</span>
        </div>
      </div>

      <!-- Row 2: Principal / Interest -->
      <div class="grid text-sm gap-2 mt-2">
        <div class="col-12 md:col-6 flex align-items-center">
          <span class="font-semibold text-300 mr-1">Principal:</span>
          <span>{{ bill.jsPrincipalDue | currency }}</span>
        </div>
        <div class="col-12 md:col-5 flex align-items-center">
          <span class="font-semibold text-300 mr-1">Interest:</span>
          <span>{{ bill.jsInterestDue | currency }}</span>
        </div>
      </div>

      <!-- Row 3: Fees / Total -->
      <div class="grid text-sm gap-2 mt-2">
        <div class="col-12 md:col-6 flex align-items-center">
          <span class="font-semibold text-300 mr-1">Fees:</span>
          <span>{{ bill.jsFeesDue | currency }}</span>
        </div>
        <div class="col-12 md:col-5 flex align-items-center">
          <span class="font-semibold text-300 mr-1">Total:</span>
          <span>{{ bill.jsTotalDue | currency }}</span>
        </div>
      </div>

      <!-- Row 4: Satisfied On (only if Paid) -->
      <div class="grid text-sm gap-2 mt-2" *ngIf="bill.isPaid() && bill.dateFullySatisfied">
        <div class="col-12 md:col-6 flex align-items-center">
          <span class="font-semibold text-300 mr-1">Satisfied On:</span>
          <span>{{ bill.dateFullySatisfied.toString() | date: 'yyyy-MM-dd' }}</span>
        </div>
      </div>
    </p-card>

    <!-- 3) Payment (Usage) Details Table -->
    <div class="text-sm mt-3">
      <span class="font-semibold text-300">Payment Details:</span>
      <p-table [value]="bill.paymentDetails || []" responsiveLayout="scroll" styleClass="mt-2">
        <ng-template pTemplate="header">
          <tr>
            <th>Deposit ID</th>
            <th>Allocated Total</th>
            <th>Allocated Principal</th>
            <th>Allocated Interest</th>
            <th>Allocated Fees</th>
            <th>Date</th>
          </tr>
        </ng-template>
        <ng-template #body let-payment>
          <tr>
            <td>{{ payment.depositId }}</td>
            <td>{{ payment.jsAllocatedTotal | currency }}</td>
            <td>{{ payment.jsAllocatedPrincipal | currency }}</td>
            <td>{{ payment.jsAllocatedInterest | currency }}</td>
            <td>{{ payment.jsAllocatedFees | currency }}</td>
            <td>{{ payment.jsDate | date: 'yyyy-MM-dd' }}</td>
          </tr>
        </ng-template>

        <ng-template #footer>
          <!-- Totals row: how much was allocated in total -->
          <tr>
            <td class="bg-blue-50" colspan="1" style="text-align: right; font-weight: bold">Allocated:</td>
            <td class="bg-blue-50" style="font-weight: bold">
              {{ bill?.jsAllocatedTotalSum | currency }}
            </td>
            <td class="bg-blue-50" style="font-weight: bold">
              {{ bill?.jsAllocatedPrincipalSum | currency }}
            </td>
            <td class="bg-blue-50" style="font-weight: bold">
              {{ bill?.jsAllocatedInterestSum | currency }}
            </td>
            <td class="bg-blue-50" style="font-weight: bold">
              {{ bill?.jsAllocatedFeesSum | currency }}
            </td>
            <td class="bg-blue-50"></td>
          </tr>

          <!-- Remaining row: original minus allocated -->
          <tr>
            <td class="bg-blue-50" colspan="1" style="text-align: right; font-weight: bold">Remaining:</td>
            <td class="bg-blue-50" style="font-weight: bold">
              {{ bill?.jsRemainingTotal | currency }}
            </td>
            <td class="bg-blue-50" style="font-weight: bold">
              {{ bill?.jsRemainingPrincipal | currency }}
            </td>
            <td class="bg-blue-50" style="font-weight: bold">
              {{ bill?.jsRemainingInterest | currency }}
            </td>
            <td class="bg-blue-50" style="font-weight: bold">
              {{ bill?.jsRemainingFees | currency }}
            </td>
            <td class="bg-blue-50"></td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </ng-container>
</p-dialog>

<!-- Ad-hoc Refund dialog -->
<p-dialog
  header="Ad-hoc Refund"
  [(visible)]="showAdhocDialog"
  [modal]="true"
  [style]="{ width: '26rem' }"
  [closable]="true"
>
  <div class="p-fluid">
    <div class="field">
      <label for="adhoc-amount">Amount</label>
      <p-inputgroup>
        <p-inputgroup-addon>$</p-inputgroup-addon>
        <p-inputNumber
          id="adhoc-amount"
          [(ngModel)]="adhocForm.amount"
          mode="decimal"
          minFractionDigits="2"
          maxFractionDigits="2"
          class="w-full"
          inputStyleClass="w-full"
        ></p-inputNumber>
      </p-inputgroup>
    </div>

    <div class="field mb-3">
      <label for="adhoc-date">Effective Date</label>
      <p-datepicker
        id="adhoc-date"
        [(ngModel)]="adhocForm.jsEffectiveDate"
        dateFormat="yy-mm-dd"
        showIcon
        appendTo="body"
        class="w-full"
        inputStyleClass="w-full"
        [style]="{ width: '100%' }"
        [inputStyle]="{ width: '100%' }"
      ></p-datepicker>
    </div>

    <div class="field flex align-items-center">
      <p-checkbox binary="true" [(ngModel)]="adhocForm.balanceImpacting" inputId="balImpact"></p-checkbox>
      <label for="balImpact" class="ml-2 mb-0">Increase principal balance</label>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" severity="secondary" (click)="showAdhocDialog = false"></p-button>
    <p-button label="Save" (click)="saveAdhocRefund()"></p-button>
  </ng-template>
</p-dialog>
